import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { cx } from 'class-variance-authority';
import { useInView, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';

import PopHighlightedFeaturesSectionContent from './PopHighlightedFeaturesSectionContent';
import PopHighlightedFeaturesSectionMedia from './PopHighlightedFeaturesSectionMedia';
import type { PopHighlightedFeaturesSectionData } from './types';

export interface PopHighlightedFeaturesSectionProps
  extends PopHighlightedFeaturesSectionData {
  readonly activeIndex: number;
  readonly index: number;
}

const PopHighlightedFeaturesSection = ({
  activeIndex,
  copy,
  footer,
  id,
  image,
  index,
  title,
  video,
}: PopHighlightedFeaturesSectionProps) => {
  const ref = useRef<HTMLElement>(null);
  const isLargeScreen = useMediaQuery(`(min-width: ${BREAKPOINTS.large}px)`);
  const shouldReduceMotion = useReducedMotion();
  const isInView = useInView(ref, { once: true });
  const isSticky = Boolean(isLargeScreen && !shouldReduceMotion);
  const isActiveSection = activeIndex === index;

  return (
    <section
      className={cx(
        'pt-6 pb-16 lg:h-screen lg:pt-16 lg:nojs:relative lg:motion-safe:js:absolute lg:motion-safe:js:top-0 lg:motion-safe:js:w-full',
        {
          'lg:motion-safe:js:pointer-events-none': !isActiveSection,
        },
      )}
      data-cy="pop-highlighted-features-section"
      data-sticky={`${isSticky}`}
      ref={ref}
      suppressHydrationWarning
    >
      <LayoutGrid className="h-full items-center" rowGap={56}>
        <GridItem
          className="lg:order-2"
          colEnd={{ sm: 'main' }}
          colStart={{ sm: 'main', lg: 12, xl: 9 }}
        >
          <PopHighlightedFeaturesSectionMedia
            activeIndex={activeIndex}
            id={id}
            image={image}
            index={index}
            isInView={isInView}
            isSticky={isSticky}
            video={video}
          />
        </GridItem>
        <GridItem
          className="lg:order-1"
          colEnd={{ sm: 'main', lg: 11, xl: 8 }}
          colStart={{ sm: 'main' }}
        >
          <PopHighlightedFeaturesSectionContent
            activeIndex={activeIndex}
            copy={copy}
            footer={footer}
            index={index}
            isInView={isInView}
            isSticky={isSticky}
            title={title}
          />
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default PopHighlightedFeaturesSection;
